<template>
  <div class="ai-custom-page-detail">
    <!-- 详情页头部 -->
    <div class="detail-header">
      <div class="header-left">
        <el-button type="text" @click="handleGoBack">
          <i class="el-icon-arrow-left" />
        </el-button>
        <span class="form-name">{{ formConfig.name }}</span>
        <span v-if="currentRevision !== null" style="font-size: 12px; color: #909399; margin-left: 8px">{{ $t('CargoMsg.Cargo_VersionNo') }}: {{ currentRevision }}</span>
      </div>
      <div class="header-right">
        <span class="last-operate-time">{{ $t('CargoMsg.Cargo_LastOperateTime') }}: {{ lastOperateTime }}</span>
        <el-button type="text" class="revision-toggle" @click="handleRevisionPanelToggle">
          <i class="el-icon-time" />
        </el-button>
        <el-button v-if="formConfig.published === PUBLISHED.DRAFT.value" type="warning" size="small" @click="handleSaveDraft">{{ $t('CargoMsg.Cargo_SaveDraft') }}</el-button>
        <el-button type="primary" size="small" @click="handlePublish">{{ $t('CargoMsg.Cargo_Publish') }}</el-button>
        <el-button size="small" @click="handleGoBack">{{ $t('CargoMsg.Cargo_Close') }}</el-button>
      </div>
    </div>
    <!-- 详情页内容 -->
    <div class="detail-content">
      <div
        class="section"
        style="
          height: 100%;
          padding: 0 5px;
          display: flex;
          flex-direction: row;
          position: relative;
        "
      >
        <!-- 表单版本面板 -->
        <transition name="slide">
          <div v-if="revisionVisible" class="revisions-panel">
            <!-- 当前表单 -->
            <div class="revision-item current" :class="{ selected: currentRevision === null }" @click="handleCurrentFormClick">
              <div class="dot" />
              <div class="revision-info">
                <div class="revision-title">{{ $t('CargoMsg.Cargo_Current') }}</div>
              </div>
            </div>
            <!-- 修订版本列表 -->
            <div
              v-for="revision in revisionList"
              :key="revision.serialNumber"
              class="revision-item"
              :class="{ selected: revision.serialNumber === currentRevision }"
              @click="handleRevisionClick(revision)"
            >
              <div class="dot" />
              <div class="revision-info">
                <div class="revision-title">
                  <span style="background: #f5f7fa; padding: 2px 4px; border-radius: 2px">
                    {{ revision.formConfig.published === PUBLISHED.PUBLISHED.value ? $t('CargoMsg.Cargo_Published') : $t('CargoMsg.Cargo_Draft') }}
                  </span>
                  <span style="margin-left: 8px">{{ new Date(revision.formConfig.dateModified).toLocaleString() }}</span>
                </div>
                <div class="revision-version">{{ $t('CargoMsg.Cargo_VersionNo') }}: {{ revision.serialNumber }}</div>
                <a class="revision-preview" @click.stop="handleRevisionPreview(revision)">{{ $t('CargoMsg.Cargo_PreviewRevision') }}</a>
              </div>
            </div>
            <el-button
              v-if="currentRevision !== null"
              type="primary"
              style="position: sticky; bottom: 10px; left: 50%; transform: translateX(-50%); width: 50%; z-index: 1000;"
              @click="handleRestoreRevision"
            >
              {{ $t('CargoMsg.Cargo_RestoreRevision') }}
            </el-button>
          </div>
        </transition>
        <!-- 左侧树形视图 -->
        <div
          class="left-tree"
          style="
            flex: 0 0 auto;
            width: 200px;
            margin: 5px 0;
            height: calc(100vh - 180px);
            overflow: hidden;
            display: flex;
            flex-direction: column;
          "
        >
          <cargo-ag-grid
            :grid-options="leftGridOptions"
            :row-data="leftFinalRowData"
            :show-side-bar="false"
            style="flex: 1;"
          />
        </div>
        <div
          style="
            flex: 0 0 auto;
            width: 2px;
            margin: 0 5px;
            background: #DCDFE6;
          "
        />
        <!-- 中间树形视图 -->
        <div
          class="middle-tree"
          style="
            flex: 0 0 auto;
            width: 300px;
            margin: 5px 0;
            height: calc(100vh - 180px);
            overflow: hidden;
            display: flex;
            flex-direction: column;
          "
        >
          <!-- 搜索框 -->
          <div class="search-box" style="padding: 10px;">
            <el-input
              v-model="searchKeyword"
              :placeholder="$t('CargoMsg.Cargo_Search')"
              prefix-icon="el-icon-search"
              clearable
              @input="handleSearch"
            />
          </div>
          <!-- 树形视图 -->
          <el-tree
            ref="middleTree"
            :data="middleTreeData"
            :props="middleTreeProps"
            node-key="id"
            default-expand-all
            :expand-on-click-node="false"
            show-checkbox
            :filter-node-method="filterNode"
            style="
              flex: 1;
              overflow: auto;
            "
            @check="handleTreeCheck"
          >
            <span slot-scope="{ node, data }" class="custom-tree-node">
              <span>{{ getLocalizedName(data) }}</span>
            </span>
          </el-tree>
        </div>
        <div
          style="
            flex: 0 0 auto;
            width: 2px;
            margin: 0 5px;
            background: #DCDFE6;
          "
        />
        <!-- 添加元素控制 -->
        <div
          class="add-controls"
          style="
            flex: 0 0 auto;
            width: 200px;
            margin: 5px 0;
            height: calc(100vh - 180px);
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            background: #fff;
          "
        >
          <el-button
            type="primary"
            style="
              width: 160px;
              margin-bottom: 20px;
            "
            :disabled="isRevision || !selectedPagePartId || !hasSelectedElements"
            @click="handleAddSelectedElements"
          >
            {{ $t('CargoMsg.Cargo_AddSelectedElements') }}
          </el-button>
          <el-select
            v-model="selectedPagePartId"
            :placeholder="$t('CargoMsg.Cargo_SelectPagePart')"
            style="width: 160px;"
          >
            <el-option
              v-for="option in localizedPagePartOptions"
              :key="option.key"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
        </div>
        <div
          style="
            flex: 0 0 auto;
            width: 2px;
            margin: 0 5px;
            background: #DCDFE6;
          "
        />
        <div
          class="debug-and-config-container"
          style="
            flex: 1;
            margin: 5px 0;
            height: calc(100vh - 180px);
            overflow: hidden;
            background: #fff;
          "
        >
          <div class="config-header">
            <span class="config-title">{{ $t('CargoMsg.Cargo_DebugAndConfig') }}</span>
            <div class="config-actions">
              <el-button type="primary" @click="handleAddPagePart">
                {{ $t('CargoMsg.Cargo_AddPagePart') }}
              </el-button>
              <el-button type="primary" @click="handleFormConfig">
                {{ $t('CargoMsg.Cargo_ConfigForm') }}
              </el-button>
              <el-button type="primary" @click="handlePreview">
                {{ $t('CargoMsg.Cargo_Debug') }}
              </el-button>
            </div>
          </div>
          <el-tabs v-model="activeTab" type="border-card" style="height: calc(100% - 50px);">
            <el-tab-pane :label="$t('CargoMsg.Cargo_VisualizationView')" name="visualization" style="height: 100%;">
              <ai-custom-page-visualization-view
                v-show="activeTab === 'visualization'"
                ref="visualizationView"
                :page-parts="pageParts"
                :is-revision="isRevision"
                :application-scene="formConfig.applicationScene"
                :business-type="formConfig.businessType"
                style="height: 100%;"
                @update:pageParts="handlePagePartsUpdate"
                @remove-page-part="removePagePart"
                @remove-element="removeElement"
              />
            </el-tab-pane>
            <el-tab-pane :label="$t('CargoMsg.Cargo_CodeView')" name="code" style="height: 100%;">
              <ai-custom-page-code-view
                v-show="activeTab === 'code'"
                ref="codeView"
                :page-parts="pageParts"
                :form-config="formConfig"
                :form-data="formData"
                :visible="activeTab === 'code' || updateFormCode"
                :is-revision="isRevision"
                style="height: 100%;"
                @update:formCode="handleFormCodeUpdate"
              />
            </el-tab-pane>
            <el-tab-pane :label="$t('CargoMsg.Cargo_ChatView')" name="chat" style="height: 100%;">
              <ai-custom-page-chat-view
                v-show="activeTab === 'chat'"
                ref="chatView"
                :page-parts="pageParts"
                :is-revision="isRevision"
                :form-uuid="formUuid"
                style="height: 100%;"
                @ruleChange="handleRuleChange"
              />
            </el-tab-pane>
          </el-tabs>
        </div>
      </div>
    </div>
    <el-dialog
      :visible.sync="previewDialog.visible"
      :title="$t('CargoMsg.Cargo_Debug')"
      :fullscreen="false"
      :show-close="true"
      :close-on-click-modal="true"
      :close-on-press-escape="false"
      custom-class="preview-dialog"
      :modal="true"
      :append-to-body="true"
      :modal-append-to-body="true"
      width="70%"
      top="20px"
      class="preview-dialog-wrapper"
    >
      <!-- 用于调试正式表单加载
        :debug="false"
        :readonly="false"
        :job-uuid="'BokTZhDHpAnSoq3W5PXRcg'"
      -->
      <ai-custom-page-data-view
        v-if="previewDialog.visible"
        :page-parts="pageParts"
        :form-config="formConfig"
        :form-data="formData"
        :debug="true"
        :readonly="false"
        :is-revision="isRevision"
        @close="previewDialog.visible = false"
        @update:formData="handleFormDataUpdate"
      />
    </el-dialog>
    <ai-custom-page-form-config
      :visible.sync="formConfigVisible"
      :form-config="formConfig"
      :is-create-mode="!formUuid"
      @confirm="handleFormConfigConfirm"
      @cancel="handleFormConfigCancel"
    />

    <!-- 页面分块配置对话框 -->
    <ai-custom-page-page-part-config
      v-if="pagePartConfigVisible"
      :visible.sync="pagePartConfigVisible"
      :business-type="formConfig.businessType"
      :page-part="currentPagePart"
      :is-create-page-part="isCreatePagePart"
      :application-scene="formConfig.applicationScene"
      @confirm="handlePagePartConfigConfirm"
      @remove-page-part="removePagePart"
    />
  </div>
</template>

<script>
import * as UICustomConstants from '@/class/cargo/model/parameters/UICustomConstants'
import AiCustomPageVisualizationView from './aiCustomPageVisualizationView.vue'
import AiCustomPageCodeView from './aiCustomPageCodeView.vue'
import AiCustomPageChatView from './aiCustomPageChatView.vue'
import AiCustomPageDataView from './aiCustomPageDataView.vue'
import AiCustomPageFormConfig from './aiCustomPageFormConfig.vue'
import AiCustomPagePagePartConfig from './aiCustomPagePagePartConfig.vue'
import { getLocalizedName, getCurrentLangCode } from '@/utils/langUtil'
import {
  BUSINESS_TYPE,
  MAX_PAGE_PARTS,
  PUBLISHED,
  DEFAULT_PAGE_PART,
  PAGE_PART_TYPE,
  DEFAULT_BUSINESS_CONFIG,
  DEFAULT_CSN_CONFIG,
  DEFAULT_CONTAINER_CONFIG,
  APPLICATION_SCENE,
  BUSINESS_BATCH_EXCLUDED_PAGE_TYPES,
  DEFAULT_AE_CARGO_INFO_CONFIG
} from '@/class/cargo/model/parameters/AiCustomPageConstants'
import { isCsnInfoPagePartType, isExcludeElement, isExcludeElementInBatchMode } from '@/class/cargo/model/utils/AiCustomPageUtil'
import { saveForm, queryFormByUuid, queryFormRevisions, restoreFormRevision } from '@/api/sysManage/aiCustomPage'
import { getLoading4Cxt } from '@/utils/utilBaseElement'
import { functionRegistry } from '@/store/modules/aiCustomPage/function-registry'
import { getBranchElementBlockList } from '@/api/sysManage/uiCustom'

export default {
  name: 'AiCustomPageDetail',
  components: {
    AiCustomPageVisualizationView,
    AiCustomPageCodeView,
    AiCustomPageChatView,
    AiCustomPageDataView,
    AiCustomPageFormConfig,
    AiCustomPagePagePartConfig
  },
  props: {
    formConfig: {
      type: Object,
      required: true
    },
    formUuid: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      // 左侧树形视图配置
      leftGridOptions: {
        rowSelection: 'single',
        suppressCellSelection: true,
        suppressContextMenu: false,
        treeData: true,
        groupDefaultExpanded: 3,
        getDataPath: data => {
          return data.path.split(',')
        },
        autoGroupColumnDef: {
          headerName: this.$t('CargoMsg.Cargo_Name'),
          field: 'name',
          width: 187,
          cellRendererParams: {
            suppressCount: true,
            suppressDoubleClickExpand: true,
            innerRenderer: params => {
              return params.value
            }
          },
          sortable: false,
          suppressMenu: true,
          suppressSizeToFit: true,
          resizable: false
        },
        columnDefs: [],
        context: {
          componentParent: this
        },
        onRowClicked: async event => {
          if (event.data.dataType === 2) {
            // 页签切换时，清空之前的勾选状态
            this.clearTreeSelection()

            this.selectedPageType = event.data.pageType
            try {
              // 直接使用API加载数据
              await this.loadSysFieldDict(event.data.pageType, this.formConfig.branchId)
              // 确保树形视图更新
              this.$nextTick(() => {
                this.loadMiddleTreeData()
              })
            } catch (error) {
              console.error('加载字段字典失败:', error)
              this.$message.error('加载字段字典失败')
            }
          }
        }
      },
      baseLeftRowData: [
        { name: this.$t('CargoMsg.Cargo_OceanExport'), path: 'SE', dataType: 1 },
        // { name: this.$t('CargoMsg.Cargo_BookingPreAudit'), path: 'SE,bookingVerify', dataType: 1 },
        // { name: this.$t('SystemMaintenance.BasicTab'), path: 'SE,bookingVerify,basic', dataType: 2, pageType: UICustomConstants.SEA_VERIFY_BOOKING_BASIC },
        // { name: this.$t('Message.BookingLabel'), path: 'SE,bookingVerify,vesselEq', dataType: 2, pageType: UICustomConstants.SEA_VERIFY_BOOKING_VESSEL },
        { name: this.$t('CargoMsg.Cargo_SeaBooking'), path: 'SE,bookingNew', dataType: 1 },
        { name: this.$t('SystemMaintenance.BasicTab'), path: 'SE,bookingNew,basic', dataType: 2, pageType: UICustomConstants.SEA_BOOKING_BASIC },
        { name: this.$t('Message.BookingLabel'), path: 'SE,bookingNew,vesselEq', dataType: 2, pageType: UICustomConstants.SEA_BOOKING_VESSEL },
        { name: 'MBL' + this.$t('SeaExport.tab'), path: 'SE,bookingNew,mbl', dataType: 2, pageType: UICustomConstants.SEA_BOOKING_MBL },
        { name: 'HBL' + this.$t('SeaExport.tab'), path: 'SE,bookingNew,hbl', dataType: 2, pageType: UICustomConstants.SEA_BOOKING_HBL, enableInstruction: true, lob: 'SE' },
        { name: this.$t('SeaExport.FormalHbl'), path: 'SE,bookingNew,virtualHbl', dataType: 2, pageType: UICustomConstants.SEA_BOOKING_VIRTUAL_HBL },
        { name: this.$t('CargoMsg.Cargo_AirExport'), path: 'AE', dataType: 1 },
        { name: this.$t('route.airExportBooking'), path: 'AE,aeBooking', dataType: 1 },
        { name: this.$t('SystemMaintenance.BasicTab'), path: 'AE,aeBooking,basic', dataType: 2, pageType: UICustomConstants.AIR_BOOKING_BASIC },
        { name: this.$t('Message.BookingLabel'), path: 'AE,aeBooking,vesselEq', dataType: 2, pageType: UICustomConstants.AIR_BOOKING_VESSEL }
      ],
      // 中间树形视图数据
      middleTreeData: [],
      searchKeyword: '',
      middleTreeProps: {
        label: 'name',
        children: 'children'
      },
      selectedPageType: null,
      pageParts: [DEFAULT_PAGE_PART(1)],
      formData: {},
      formCode: '',
      updateFormCode: false,
      selectedPagePartId: null,
      selectedElements: [],
      hasSelectedElements: false,
      activeTab: 'visualization',
      previewDialog: {
        visible: false
      },
      // 初始化最后操作时间为之前保存的更新时间
      lastOperateTime: this.formConfig.dateModified
        ? new Date(this.formConfig.dateModified).toLocaleString()
        : new Date().toLocaleString(),
      PUBLISHED,
      loading: null,
      currentSysFieldDict: null, // 当前选中页签对应的系统字段字典
      currentPageType: null, // 当前选中页签
      formConfigVisible: false,
      revisionVisible: false,
      currentConfig: null,
      currentRevision: null,
      revisionList: null,
      CODE_GENERATION_DELAY: 1000,
      // 代码生成信号量
      codeGenerationSemaphore: null,
      codeGenerationResolve: null,
      pagePartConfigVisible: false,
      currentPagePart: null,
      isCreatePagePart: false
    }
  },
  computed: {
    leftRowData() {
      // 根据应用场景过滤左侧树形数据
      if (this.formConfig.applicationScene === APPLICATION_SCENE.BUSINESS_BATCH.value) {
        // 业务批量模式下，过滤掉被排除的页签类型
        return this.baseLeftRowData.filter(item => {
          // 如果是数据类型为2（页签），检查是否需要排除
          if (item.dataType === 2 && item.pageType) {
            return !BUSINESS_BATCH_EXCLUDED_PAGE_TYPES.has(item.pageType)
          }
          return true
        })
      }
      // 其他模式返回完整数据
      return this.baseLeftRowData
    },
    localizedPagePartOptions() {
      const currentLangCode = getCurrentLangCode()
      const options = this.pageParts
        .filter(pagePart => pagePart.blockType === PAGE_PART_TYPE.BUSINESS_ELEMENTS.value) // 只显示业务字段分块
        .map(pagePart => {
          const language = pagePart.languageList.find(l => l.language === currentLangCode)
          return {
            key: `${pagePart.id}-${pagePart.blockType}`,
            label: language ? language.name : pagePart.name,
            value: pagePart.id
          }
        })
      console.log('页面部件选项:', options)
      return options
    },
    isRevision() {
      return this.currentRevision !== null
    },
    leftFinalRowData() {
      const lob = Object.values(BUSINESS_TYPE).find(t => t.value === this.formConfig.businessType)?.lob
      if (lob) {
        return this.leftRowData.filter(item => item.path.split(',')[0] === lob)
      }
      return this.leftRowData
    }
  },
  watch: {
    searchKeyword(val) {
      this.$refs.middleTree.filter(val)
    },
    revisionVisible(val) {
      if (val) {
        this.loadRevisions()
      }
    },
    localizedPagePartOptions: {
      handler(newOptions) {
        console.log('[详情页] 页面分块选项:', { newOptions, selectedPagePartId: this.selectedPagePartId })
        // 当选项为空时，重置selectedPagePartId为null
        if (!newOptions || newOptions.length === 0) {
          this.selectedPagePartId = null
          return
        }
        // 如果选中的分块id不在新的选项中，则任选一个设置
        if (!newOptions.some(option => option.value === this.selectedPagePartId)) {
          this.selectedPagePartId = newOptions[0].value
        }
      },
      immediate: true
    }
  },
  created() {
    console.log('=== AiCustomPageDetail 组件创建 ===')
    console.log('formConfig:', this.formConfig)
    console.log('applicationScene:', this.formConfig.applicationScene)
    // 初始化页面部件
    this.initializePageParts()
    // 初始化表单数据
    try {
      this.formData = this.formConfig.formDataJson ? JSON.parse(this.formConfig.formDataJson) : {}
    } catch (error) {
      console.error('解析表单数据失败:', error)
      this.formData = {}
    }
    // 初始化规则
    this.initializeFormCode()
  },
  methods: {
    // 初始化表单代码和规则
    initializeFormCode() {
      // 清除函数注册表中的所有规则
      functionRegistry.clearFormFunctions(this.formUuid)

      // 初始化表单代码
      this.formCode = this.formConfig.formCode || ''

      // 修复单引号转义问题：将 \' 替换为 '
      if (this.formCode) {
        this.formCode = this.formCode.replace(/\\'/g, "'")
      }

      if (!this.formCode) return

      console.log('[详情页] 开始解析表单代码中的规则...')
      try {
        // 使用正则表达式匹配规则块，支持多行JSON
        const ruleRegex = /<!--\s*\n\s*({[\s\S]*?})\s*\n\s*-->\s*\n\s*<script>\s*\n([\s\S]*?)<\/script>/g
        let match
        const rules = []

        while ((match = ruleRegex.exec(this.formCode)) !== null) {
          const jsonContent = match[1].trim()
          const functionCode = match[2].trim()
          console.log('[详情页] 匹配规则JSON元信息:', jsonContent)
          console.log('[详情页] 匹配函数代码:', functionCode)

          // 尝试解析JSON数据
          try {
            console.log('[详情页] 解析JSON数据:', jsonContent)
            const ruleData = JSON.parse(jsonContent)

            if (ruleData && ruleData.id) {
              console.log(`[详情页] 加载规则: ${ruleData.id} (${ruleData.type})`)
              // 使用script中的函数代码，这个更方便编辑
              ruleData.code = functionCode
              rules.push(ruleData)
              // 注册到函数注册表
              functionRegistry.registerFunction(
                this.formUuid,
                ruleData.id,
                ruleData.code,
                ruleData.type,
                ruleData.metadata
              )
            }
          } catch (jsonError) {
            console.error('[详情页] JSON解析错误:', jsonError)
          }
        }

        console.log(`[详情页] 规则加载完成，共 ${rules.length} 条规则`)
      } catch (error) {
        console.error('[详情页] 解析规则失败:', error)
      }
    },

    // 更新表单配置
    async updateFormConfig(newConfig) {
      // 如果是编辑模式（有formUuid），调用API获取数据
      if (this.formUuid) {
        try {
          const response = await queryFormByUuid({ uuid: this.formUuid })
          if (response && response.data) {
            this.formConfig = {
              ...response.data,
              ...newConfig // 使用新配置覆盖查询结果
            }
          }
        } catch (error) {
          console.error('获取表单配置失败:', error)
          this.$message.error(this.$t('CargoMsg.Cargo_GetFormConfigFailed'))
        }
      } else {
        // 如果是创建模式，直接使用配置
        this.formConfig = newConfig
      }
      console.log('表单配置已更新:', this.formConfig)
    },

    // 初始化字段字典
    async initFieldDict() {
      try {
        console.log('开始初始化字段字典...')
        // 不再需要初始化，等待用户选择数据类型
      } catch (error) {
        console.error('初始化字段字典失败:', error)
        this.$message.error('初始化字段字典失败')
      }
    },
    // 初始化页面部件
    initializePageParts() {
      // 如果formConfig.pageParts存在，使用它
      if (this.formConfig.pageParts && this.formConfig.pageParts.length > 0) {
        // 转换数据，使用serialNumber作为id
        this.pageParts = this.formConfig.pageParts.map(pagePart => ({
          ...pagePart,
          languageList: pagePart.languageList || DEFAULT_PAGE_PART(pagePart.serialNumber).languageList,
          id: pagePart.serialNumber
        }))

        // 针对特定类型分块，如果配置为空，则初始化
        this.pageParts.forEach(pagePart => {
          if (pagePart.blockType === PAGE_PART_TYPE.BUSINESS_ELEMENTS.value && !pagePart.businessConfig) {
            pagePart.businessConfig = DEFAULT_BUSINESS_CONFIG()
          } else if (isCsnInfoPagePartType(pagePart.blockType) && !pagePart.csnConfig) {
            pagePart.csnConfig = DEFAULT_CSN_CONFIG()
          } else if (pagePart.blockType === PAGE_PART_TYPE.CONTAINER_INFO.value && !pagePart.containerConfig) {
            pagePart.containerConfig = DEFAULT_CONTAINER_CONFIG()
          } else if (pagePart.blockType === PAGE_PART_TYPE.AE_CARGO_INFO.value && !pagePart.aeCargoInfoConfig) {
            pagePart.aeCargoInfoConfig = DEFAULT_AE_CARGO_INFO_CONFIG()
          }
        })

        return
      }

      // 否则使用默认值
      const defaultPagePart = DEFAULT_PAGE_PART(1)
      this.pageParts = [defaultPagePart]

      // 初始化formConfig.pageParts
      this.$set(this.formConfig, 'pageParts', [...this.pageParts])
    },
    // 过滤节点
    filterNode(value, data) {
      if (!value) return true
      const name = getLocalizedName(data).toLowerCase()
      return name.includes(value.toLowerCase())
    },
    // 加载中间树形视图数据
    loadMiddleTreeData() {
      if (!this.currentPageType) return
      // 获取原始数据
      const originalData = this.currentSysFieldDict
      if (!originalData || !originalData.data || !originalData.data.sysBlocks) return

      // 找到当前选中的页签名称
      const currentTab = this.leftRowData.find(item => item.pageType === this.currentPageType)
      if (!currentTab) return

      const blocks = originalData.data.branchBlocks
        ? originalData.data.branchBlocks
        : originalData.data.sysBlocks

      // 创建树形结构
      this.middleTreeData = [{
        id: 'root',
        name: currentTab.name,
        children: blocks
          .sort((a, b) => {
            if (a.pagePartPosition !== b.pagePartPosition) {
              return a.pagePartPosition - b.pagePartPosition
            }
            return a.serialNumber - b.serialNumber
          })
          .map(block => {
            if (!block || !block.pagePart) return null
            const pagePart = block.pagePart
            const uiCustomElementList = block.uiCustomElementList
            return {
              id: `part_${pagePart.module}_${pagePart.pagePartName}`,
              name: this.getLocalizedName(pagePart),
              languageList: pagePart.languageList,
              children: uiCustomElementList
                .filter(uiCustomElement => uiCustomElement.isDisplay)
                .map(uiCustomElement => {
                  const element = uiCustomElement.pagePartElement
                  if (!element || !element.elementName || isExcludeElementInBatchMode(element, this.formConfig.applicationScene)) {
                    return null
                  }

                  // 应用别名替换逻辑
                  let resultLanguageList = element.languageList || []
                  if (uiCustomElement.languageList && uiCustomElement.languageList.length > 0) {
                    // 过滤出有非空名称的别名语言项
                    const aliasLanguages = uiCustomElement.languageList.filter(alias => alias.name && alias.name.trim())

                    // 替换匹配的语言项
                    resultLanguageList = resultLanguageList.map(langItem => {
                      const aliasItem = aliasLanguages.find(alias => alias.language === langItem.language)
                      return aliasItem || langItem
                    })
                  }

                  return {
                    id: `element_${pagePart.module}_${pagePart.pagePartName}_${element.elementName}`,
                    name: this.getLocalizedName(element),
                    languageList: resultLanguageList,
                    elementName: element.elementName,
                    pageType: element.pageType,
                    pagePartElementUuid: element.uuid,
                    isDefaultRequire: uiCustomElement.isRequire
                  }
                })
                .filter(Boolean)
            }
          })
          .filter(Boolean)
          .filter(block => block.children.length > 0)
      }]
    },
    // 处理搜索
    handleSearch(value) {
      this.searchKeyword = value
    },
    // 添加页面部件
    handleAddPagePart() {
      if (this.isRevision) return
      if (this.pageParts.length >= MAX_PAGE_PARTS) {
        this.$message.warning(this.$t('CargoMsg.Cargo_MaxPagePartsReached', { 0: MAX_PAGE_PARTS }))
        return
      }
      let counter = 1
      let newName = `页面区域${counter}`
      // 检查名称是否重复，如果重复则递增计数器直到找到唯一名称
      while (this.pageParts.some(p => p.name === newName || p.id === counter)) {
        counter++
        newName = `页面区域${counter}`
      }

      const newPagePart = DEFAULT_PAGE_PART(counter)
      console.log('添加页面部件:', { id: newPagePart.id, uuid: newPagePart.uuid, name: newPagePart.name })
      this.pageParts.push(newPagePart)
      // 选中新添加的页面部件
      this.selectedPagePartId = newPagePart.id
      // 立即打开配置对话框
      this.currentPagePart = { ...newPagePart }
      this.pagePartConfigVisible = true
      this.isCreatePagePart = true
    },
    // 预览和设置
    handlePreview() {
      if (this.isRevision) return
      this.previewDialog.visible = true
    },
    // 开始编辑页面部件名称
    startEdit(pagePart) {
      // 重置所有页面部件的编辑状态
      this.pageParts.forEach(p => {
        p.isEditing = false
        p.editingName = ''
      })
      // 设置当前页面部件的编辑状态
      pagePart.isEditing = true
      pagePart.editingName = pagePart.name
      this.$nextTick(() => {
        const input = this.$el.querySelector('.page-part-title .el-input__inner')
        if (input) {
          input.focus()
        }
      })
    },
    // 保存页面部件名称
    savePagePartName(pagePart) {
      if (pagePart.editingName.trim()) {
        pagePart.name = pagePart.editingName
      }
      this.cancelEdit(pagePart)
    },
    // 取消编辑
    cancelEdit(pagePart) {
      pagePart.isEditing = false
      pagePart.editingName = ''
    },
    // 移除页面部件
    removePagePart(pagePart, isCreatePagePart = false) {
      if (this.pageParts.length <= 1) {
        this.$message.warning(this.$t('CargoMsg.Cargo_NeedAtLeastOnePagePart'))
        return
      }

      if (isCreatePagePart) {
        const index = this.pageParts.findIndex(p => p.id === pagePart.id)
        if (index > -1) {
          this.pageParts.splice(index, 1)
        }
        return
      }

      this.$confirm(this.$t('CargoMsg.Cargo_ConfirmDeleteBlock'), this.$t('Message.WarmReminder'), {
        confirmButtonText: this.$t('CargoMsg.Cargo_Confirm'),
        cancelButtonText: this.$t('CargoMsg.Cargo_Cancel'),
        type: 'warning'
      }).then(() => {
        console.log('移除页面部件:', { id: pagePart.id, name: pagePart.name })
        const index = this.pageParts.findIndex(p => p.id === pagePart.id)
        if (index > -1) {
          this.pageParts.splice(index, 1)
          // 如果移除的是当前选中的页面部件，则选中第一个页面部件
          if (pagePart.id === this.selectedPagePartId) {
            this.selectedPagePartId = null
          }
        }
      }).catch(() => {
        // 用户取消操作，不做任何处理
      })
    },
    // 移除元素
    removeElement(pagePart, element) {
      console.log('从页面部件移除元素:', {
        pagePartId: pagePart.id,
        pagePartName: pagePart.name,
        element: { name: element.elementName, languageList: element.languageList }
      })
      const index = pagePart.elements.findIndex(e => e.elementName === element.elementName)
      if (index > -1) {
        pagePart.elements.splice(index, 1)
      }
    },
    // 清空树形选择状态
    clearTreeSelection() {
      // 清空树形控件的勾选状态
      if (this.$refs.middleTree) {
        this.$refs.middleTree.setCheckedKeys([])
      }
      // 清空已选中的元素数组
      this.selectedElements = []
      this.hasSelectedElements = false
      // 清空搜索框
      this.searchKeyword = ''
      console.log('页签切换：已清空树形勾选状态、选中元素数据和搜索框')
    },
    // 处理树节点选中状态变化
    handleTreeCheck(data, checked) {
      const checkedNodes = this.$refs.middleTree.getCheckedNodes()
      this.selectedElements = checkedNodes
        .filter(node => !node.children && this.filterNode(this.searchKeyword, node)) // 只选择叶子节点（元素）且匹配搜索条件
        .map(node => ({
          elementName: node.elementName,
          pageType: node.pageType,
          pagePartElementUuid: node.pagePartElementUuid,
          languageList: node.languageList,
          isDefaultRequire: node.isDefaultRequire,
          isReadonly: false,
          isRequire: node.isDefaultRequire
        }))
      this.hasSelectedElements = this.selectedElements.length > 0
    },
    // 添加选中的元素到页面部件
    handleAddSelectedElements() {
      if (!this.selectedPagePartId || !this.hasSelectedElements) return

      const targetPagePart = this.pageParts.find(p => p.id === this.selectedPagePartId)
      if (!targetPagePart) return

      console.log('添加元素到页面部件:', {
        targetPagePartId: targetPagePart.id,
        targetPagePartName: targetPagePart.name,
        elements: this.selectedElements
      })

      // 添加新元素，避免重复
      this.selectedElements.forEach(element => {
        if (!this.pageParts.some(pagePart =>
          pagePart.elements.some(e => e.elementName === element.elementName)
        )) {
          targetPagePart.elements.push(element)
        }
      })

      // 清空选择
      this.$refs.middleTree.setCheckedKeys([])
      this.selectedElements = []
      this.hasSelectedElements = false

      this.$message.success(this.$t('CargoMsg.Cargo_ElementsAddedSuccessfully'))
    },
    // 获取本地化名称
    getLocalizedName(data) {
      return getLocalizedName(data)
    },
    async handleSaveDraft() {
      if (this.isRevision) return
      let loading = null
      try {
        loading = getLoading4Cxt(this)

        // 等待代码生成完成
        await this.waitForCodeGeneration()

        // 收集所有数据到formConfig
        const formConfigToSave = this.collectFormConfig()

        // 调用保存API
        const response = await saveForm(formConfigToSave)
        if (response && response.data) {
          // 获取保存后的完整表单配置
          const queryResponse = await queryFormByUuid({ uuid: response.data.uuid })
          if (queryResponse && queryResponse.data) {
            this.formConfig = { ...queryResponse.data }
            this.formCode = queryResponse.data.formCode
            // 修复单引号转义问题：将 \' 替换为 '
            if (this.formCode) {
              this.formCode = this.formCode.replace(/\\'/g, "'")
            }
            // 解析调试数据，如果报错则容错并输出日志
            try {
              this.formData = JSON.parse(queryResponse.data.formDataJson)
            } catch (error) {
              console.info('解析调试数据失败:', error)
              this.formData = {}
            }
            this.initializePageParts()

            // 对于创建模式，表单初始uuid为空，需要把生成规则映射到新的表单uuid中
            const newFormUuid = queryResponse.data.uuid
            if (!this.formUuid) {
              functionRegistry.copyFormFunctions(null, newFormUuid)
              functionRegistry.clearFormFunctions(null)

              this.copyChatMessages(null, newFormUuid)
            }
            this.formUuid = newFormUuid
          }
          this.lastOperateTime = new Date().toLocaleString()
          // 如果当前显示修订记录列表，需要重新加载
          if (this.revisionVisible) {
            await this.loadRevisions()
          }
          this.$message.success(this.$t('CargoMsg.Cargo_SaveSucceed'))
        }
      } catch (error) {
        console.error('保存表单失败：', error)
        this.$message.error(this.$t('CargoMsg.Cargo_SaveFailed'))
      } finally {
        if (loading) {
          loading.close()
        }
      }
    },
    async handlePublish() {
      if (this.isRevision) return
      let loading = null
      try {
        loading = getLoading4Cxt(this)

        // 等待代码生成完成
        await this.waitForCodeGeneration()

        const formConfigToSave = {
          ...this.collectFormConfig(),
          published: PUBLISHED.PUBLISHED.value
        }

        const saveResponse = await saveForm(formConfigToSave)
        if (saveResponse && saveResponse.data) {
          // 获取发布后的完整表单配置
          const queryResponse = await queryFormByUuid({ uuid: saveResponse.data.uuid })
          if (queryResponse && queryResponse.data) {
            this.formConfig = { ...queryResponse.data }
            this.formCode = queryResponse.data.formCode
            // 修复单引号转义问题：将 \' 替换为 '
            if (this.formCode) {
              this.formCode = this.formCode.replace(/\\'/g, "'")
            }
            this.formData = JSON.parse(queryResponse.data.formDataJson)
            this.initializePageParts()

            // 对于创建模式，表单初始uuid为空，需要把生成规则映射到新的表单uuid中
            const newFormUuid = queryResponse.data.uuid
            if (!this.formUuid) {
              functionRegistry.copyFormFunctions(null, newFormUuid)
              functionRegistry.clearFormFunctions(null)

              this.copyChatMessages(null, newFormUuid)
            }
            this.formUuid = newFormUuid
          }
          this.lastOperateTime = new Date().toLocaleString()
          // 如果当前显示修订记录列表，需要重新加载
          if (this.revisionVisible) {
            await this.loadRevisions()
          }
          this.$message.success(this.$t('CargoMsg.Cargo_PublishSuccess'))
        }
      } catch (error) {
        console.error('发布表单失败：', error)
        this.$message.error(this.$t('CargoMsg.Cargo_PublishFailed'))
      } finally {
        if (loading) {
          loading.close()
        }
      }
    },
    // 收集所有数据到formConfig
    collectFormConfig() {
      // 确保formConfig符合UiCustomFormVO格式
      const formConfig = {
        ...this.formConfig,

        name: this.formConfig.name,
        remarks: this.formConfig.remarks,

        enabled: this.formConfig.enabled,
        published: this.formConfig.published,
        branchId: this.formConfig.branchId,

        formCode: this.formCode,
        formDataJson: JSON.stringify(this.formData || {}),

        uuid: this.formUuid,

        // 表单分块列表
        pageParts: this.pageParts.map(pagePart => {
          const pagePartData = {
            name: pagePart.name, // 区域名称
            serialNumber: this.pageParts.indexOf(pagePart) + 1, // 排序序号

            blockType: pagePart.blockType,
            languageList: pagePart.languageList,
            businessConfig: pagePart.businessConfig,
            csnConfig: pagePart.csnConfig,
            uploadConfig: pagePart.uploadConfig,

            uuid: pagePart.uuid,
            formUuid: this.formUuid,

            elements: pagePart.elements.map(element => {
              // 构建element对象
              const elementData = {
                elementName: element.elementName, // 元素名称
                pageType: element.pageType, // 页签类型
                pagePartElementUuid: element.pagePartElementUuid, // 分块元素字典UUID
                serialNumber: pagePart.elements.indexOf(element) + 1, // 排序序号

                isRequire: element.isRequire, // 是否必填
                isReadonly: element.isReadonly, // 是否只读

                uuid: element.uuid,
                blockUuid: pagePart.uuid,
                formUuid: this.formUuid
              }

              return elementData
            })
          }

          if (pagePart.blockType === PAGE_PART_TYPE.BUSINESS_ELEMENTS.value) {
            if (!pagePart.businessConfig) {
              pagePart.businessConfig = DEFAULT_BUSINESS_CONFIG()
            }
            pagePartData.blockConfigJson = JSON.stringify(pagePart.businessConfig)
          } else if (isCsnInfoPagePartType(pagePart.blockType)) {
            if (!pagePart.csnConfig) {
              pagePart.csnConfig = DEFAULT_CSN_CONFIG()
            }
            // 如果区块类型是收发通信息类型且收发通配置不为空，则保存到blockConfigJson值
            pagePartData.blockConfigJson = JSON.stringify(pagePart.csnConfig)
          } else if (pagePart.blockType === PAGE_PART_TYPE.CONTAINER_INFO.value) {
            if (!pagePart.containerConfig) {
              pagePart.containerConfig = DEFAULT_CONTAINER_CONFIG()
            }
            pagePartData.blockConfigJson = JSON.stringify(pagePart.containerConfig)
          } else if (pagePart.blockType === PAGE_PART_TYPE.AE_CARGO_INFO.value) {
            if (!pagePart.aeCargoInfoConfig) {
              pagePart.aeCargoInfoConfig = DEFAULT_AE_CARGO_INFO_CONFIG()
            }
            pagePartData.blockConfigJson = JSON.stringify(pagePart.aeCargoInfoConfig)
          } else {
            // 对于其他类型的分块，则把分块高级配置置空
            pagePartData.blockConfigJson = null
          }

          return pagePartData
        })
      }

      return formConfig
    },
    handlePagePartsUpdate(newPageParts, updatedPagePart) {
      console.log('[调试] 页面部件更新 - 更新前:', this.pageParts)
      console.log('[调试] 页面部件更新 - 新数据:', newPageParts)
      this.$set(this, 'pageParts', newPageParts)
      // 如果单个页面部件的配置更新了，调用handlePagePartUpdate处理
      if (updatedPagePart) {
        this.handlePagePartUpdate(updatedPagePart)
      }
    },
    handlePagePartUpdate(updatedPagePart) {
      console.log('[调试] 页面部件更新 - 更新的部件:', updatedPagePart)
      // 创建新的页面部件数组，确保响应式更新
      const updatedPageParts = this.pageParts.map(p => {
        if (p.id === updatedPagePart.id) {
          return { ...p, blockType: updatedPagePart.blockType }
        }
        return p
      })
      this.$set(this, 'pageParts', updatedPageParts)

      // 如果当前选中的页面部件类型改变为非业务字段分块，需要重新选择
      if (updatedPagePart.blockType !== PAGE_PART_TYPE.BUSINESS_ELEMENTS.value &&
          this.selectedPagePartId === updatedPagePart.id) {
        // 查找第一个业务字段分块
        const normalPagePart = this.pageParts.find(p => p.blockType === PAGE_PART_TYPE.BUSINESS_ELEMENTS.value)
        if (normalPagePart) {
          this.selectedPagePartId = normalPagePart.id
        } else {
          this.selectedPagePartId = null
          this.selectedElements = []
          this.hasSelectedElements = false
        }
      }
    },
    handleFormCodeUpdate(newCode) {
      const startTime = performance.now()
      const maxCodeLength = 1000
      console.log('[详情页] 收到update:formCode事件，开始处理，代码长度:', newCode.length)
      console.log('[详情页] 表单代码已更新:', newCode.slice(0, maxCodeLength) + (newCode.length > maxCodeLength ? '...' : ''))

      // 更新formConfig和formCode
      this.$set(this.formConfig, 'formCode', newCode)
      this.formCode = newCode

      console.log('[详情页] 开始重新初始化规则...')
      // 重新初始化规则
      this.initializeFormCode()
      console.log('[详情页] 规则重新初始化完成')

      // 如果有等待代码生成的promise，则解析它
      if (this.codeGenerationResolve) {
        console.log('[详情页] 解析等待代码生成的promise')
        this.codeGenerationResolve()
        this.codeGenerationResolve = null
        this.codeGenerationSemaphore = null
      }

      console.log('[详情页] handleFormCodeUpdate处理完成，总耗时:', performance.now() - startTime, 'ms')
    },
    // 处理表单数据更新
    handleFormDataUpdate(newData) {
      console.log('[调试] 表单数据已更新:', newData)
      this.formData = newData
    },
    handleFormConfig() {
      if (this.isRevision) return
      this.formConfigVisible = true
    },
    handleFormConfigConfirm(formConfig) {
      this.formConfig = { ...formConfig }
      this.formConfigVisible = false
    },
    handleFormConfigCancel() {
      this.formConfigVisible = false
    },
    // 加载系统字段字典
    async loadSysFieldDict(pageType, branchId) {
      try {
        console.log('加载系统字段字典数据 pageType: ' + pageType + ' branchId: ' + branchId)
        const response = await getBranchElementBlockList({
          pageType: pageType,
          branchId: branchId
        })
        this.currentSysFieldDict = response
        this.currentPageType = pageType
        return response
      } catch (error) {
        console.error('加载系统字段字典失败:', error)
        throw error
      }
    },
    // 处理返回操作
    async handleGoBack() {
      if (this.currentRevision !== null) {
        this.handleCurrentFormClick()
      }
      this.$confirm(this.$t('CargoMsg.Carog_ConfirmSaveBeforeGoBack'), this.$t('Message.WarmReminder'), {
        confirmButtonText: this.$t('CargoMsg.Cargo_Save'),
        cancelButtonText: this.$t('CargoMsg.Cargo_Cancel'),
        type: 'warning'
      }).then(async() => {
        if (this.formConfig.published === PUBLISHED.PUBLISHED.value) {
          await this.handlePublish()
        } else {
          await this.handleSaveDraft()
        }
        setTimeout(() => {
          this.$emit('goBack')
        }, 3000)
      }).catch(() => {
        // 不执行保存
        this.$emit('goBack')
      })
    },
    async loadRevisions() {
      if (!this.formConfig?.uuid) return
      const res = await queryFormRevisions({ uuid: this.formConfig.uuid })
      if (res?.data) {
        this.revisionList = res.data.map(item => ({
          ...item,
          formConfig: JSON.parse(item.formJson)
        }))
      }
    },
    // 保存当前状态
    saveCurrentState() {
      this.currentConfig = {
        formConfig: { ...this.formConfig },
        formCode: this.formCode,
        formData: { ...this.formData },
        pageParts: [...this.pageParts]
      }
    },
    // 恢复保存的状态
    restoreSavedState() {
      this.formConfig = { ...this.currentConfig.formConfig }
      this.formCode = this.currentConfig.formCode
      this.formData = { ...this.currentConfig.formData }
      this.pageParts = [...this.currentConfig.pageParts]
      this.currentConfig = null
    },
    // 加载修订版本状态
    loadRevisionState(revision) {
      const formConfig = { ...revision.formConfig }
      const formCode = revision.formConfig.formCode
      const formData = JSON.parse(revision.formConfig.formDataJson)
      const pageParts = [...revision.formConfig.pageParts]
      this.formConfig = formConfig
      this.formCode = formCode
      this.formData = formData
      this.pageParts = pageParts
    },
    handleCurrentFormClick() {
      if (this.currentRevision === null) return // 已经在当前表单，不做操作
      // 恢复之前保存的状态
      this.restoreSavedState()
      this.currentRevision = null
    },
    handleRevisionClick(revision) {
      console.log('[调试] 当前点击表单版本:', revision)

      // 如果点击的是当前选中的版本，不做任何操作
      if (this.currentRevision === revision.serialNumber) return

      if (this.currentRevision === null) {
        // 当前在编辑表单，点击了某个修订版本
        this.saveCurrentState()
        this.loadRevisionState(revision)
        this.currentRevision = revision.serialNumber
      } else {
        // 当前在查看修订版本，点击了其他修订版本
        this.loadRevisionState(revision)
        this.currentRevision = revision.serialNumber
      }
    },
    handleRevisionPanelToggle() {
      if (this.revisionVisible) {
        this.revisionList = null
      }
      this.revisionVisible = !this.revisionVisible
    },
    handleRevisionPreview(revision) {
      this.handleRevisionClick(revision)
      this.previewDialog.visible = true
    },
    async handleRestoreRevision() {
      if (!this.currentRevision) return
      try {
        await this.$confirm(this.$t('CargoMsg.Cargo_ConfirmRestoreRevision'), this.$t('Message.WarmReminder'), {
          confirmButtonText: this.$t('CargoMsg.Cargo_Confirm'),
          cancelButtonText: this.$t('CargoMsg.Cargo_Cancel'),
          type: 'warning'
        })
        this.loading = getLoading4Cxt()
        const response = await restoreFormRevision({
          formUuid: this.formConfig.uuid,
          serialNumber: this.currentRevision
        })
        if (response?.data) {
          this.$message.success(this.$t('CargoMsg.Cargo_RestoreSucceed'))
          // 刷新加载表单数据
          const queryResponse = await queryFormByUuid({ uuid: this.formConfig.uuid })
          console.log('queryResponse', queryResponse)
          if (queryResponse?.data) {
            this.currentConfig = {
              formConfig: { ...queryResponse.data },
              formCode: queryResponse.data.formCode,
              formData: JSON.parse(queryResponse.data.formDataJson),
              pageParts: [...queryResponse.data.pageParts]
            }
            this.handleCurrentFormClick()
            this.handleRevisionPanelToggle()
          }
        }
      } catch (error) {
        if (error === 'cancel' || error === 'close') return
        console.error('还原版本失败:', error)
        this.$message.error(this.$t('CargoMsg.Cargo_RestoreFailed'))
      } finally {
        this.loading?.close()
      }
    },
    async waitForCodeGeneration() {
      const startTime = performance.now()
      console.log('[详情页] 开始等待代码生成...')

      // 如果代码标签页是激活的，代码生成会立即发生，不需要等待
      if (this.activeTab === 'code') {
        console.log('[详情页] 代码标签页已激活，无需等待，耗时:', performance.now() - startTime, 'ms')
        return Promise.resolve()
      }

      // 触发代码生成
      this.triggerCodeGeneration()

      console.log('[详情页] 等待代码生成完成...')
      // 返回promise，让调用者等待
      await this.codeGenerationSemaphore
      console.log('[详情页] 代码生成完成，等待promise已解析')

      await new Promise(resolve => setTimeout(resolve, this.CODE_GENERATION_DELAY))
      console.log('[详情页] 等待代码生成完成，总耗时:', performance.now() - startTime, 'ms')
    },
    // 当uuid变化时，复制聊天消息
    copyChatMessages(oldFormUuid, newFormUuid) {
      if (oldFormUuid === newFormUuid) return
      try {
        // 获取当前会话ID
        const sessionId = window.sessionStorage.key(0)?.split('_')[0] || 'default'
        const oldKey = `aiCustomPageChatMessages_${sessionId}_${oldFormUuid}`
        const newKey = `aiCustomPageChatMessages_${sessionId}_${newFormUuid}`
        // 从旧键获取消息
        const oldMessages = sessionStorage.getItem(oldKey)
        if (oldMessages) {
          // 复制到新键
          sessionStorage.setItem(newKey, oldMessages)
          // 清除旧键
          sessionStorage.removeItem(oldKey)
          console.log('[聊天消息] 已从', oldKey, '复制到', newKey)
        }
      } catch (error) {
        console.error('[聊天消息] 复制失败:', error)
      }
    },
    handlePagePartConfigConfirm(updatedPagePart) {
      this.pagePartConfigVisible = false
      this.isCreatePagePart = false
      this.handlePagePartUpdate(updatedPagePart)
    },
    handleRuleChange() {
      console.log('[详情页] 收到ruleChange事件，开始触发代码生成')

      // 触发代码生成
      this.triggerCodeGeneration()
    },

    // 触发代码生成的方法
    triggerCodeGeneration() {
      console.log('[详情页] 触发代码生成')
      this.updateFormCode = true

      // 创建新的promise等待代码生成
      this.codeGenerationSemaphore = new Promise((resolve) => {
        this.codeGenerationResolve = resolve
      })

      console.log('[详情页] 等待代码生成完成...')
      // 等待代码生成完成
      this.codeGenerationSemaphore.then(() => {
        console.log('[详情页] 代码生成完成，设置updateFormCode = false')
        this.updateFormCode = false
      }).catch(error => {
        console.error('[详情页] 代码生成等待失败:', error)
        this.updateFormCode = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.ai-custom-page-detail {
  padding: 3px;
  .detail-header {
    margin-top: 5px;
    margin-bottom: 5px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    .header-left {
      display: flex;
      align-items: center;
      gap: 8px;

      .el-button {
        padding: 0;
        font-size: 20px;
      }

      .form-name {
        font-size: 16px;
        font-weight: 500;
        color: #303133;
      }
    }
    .header-right {
      display: flex;
      align-items: center;
      gap: 10px;
      .last-operate-time {
        margin-right: 0;
        color: #606266;
      }
      .revision-toggle {
        padding: 0;
        color: #909399;
        &:hover {
          color: #606266;
        }
        .el-icon-time {
          padding: 0;
        }
      }
    }
  }
  .detail-content {
    background: #fff;
    padding: 20px;
    border-radius: 4px;
  }
  .left-tree {
    background: #fff;
    border: 1px solid #DCDFE6;
    border-radius: 4px;
    .cargo-ag-grid {
      height: 100%;
      ::v-deep .ag-theme-balham {
        .ag-header {
          color: rgba(51,51, 51,.8)!important;
          border: none;
        }
        .ag-header-row {
          border-style: solid;
          border-color: #DCDFE6;
          box-sizing: border-box;
          border-width: 0 0 1px;
        }
        .ag-root {
          border: none;
        }
        .ag-row {
          border: none!important;
        }
        .ag-cell {
          user-select: text;
          border-right: 1px solid #DBDFE6;
          padding-left: 5px!important;
        }
        .ag-row-even {
          background-color: #fff;
        }
        .ag-row-odd {
          background-color: #F7F7F7;
        }
        .ag-row-hover {
          background-color: #9bddff !important;
        }
        .ag-row-selected {
          background-color: #9bddff;
          &.ag-row-focus, &.shift-select, &.ctrl-select {
            background-color: #6abdff !important;
          }
        }
      }
    }
  }
  .middle-tree {
    background: #fff;
    border: 1px solid #DCDFE6;
    border-radius: 4px;
    .search-box {
      border-bottom: 1px solid #DCDFE6;
      background-color: #F7F7F7;
      padding: 10px;
      .el-input {
        ::v-deep .el-input__inner {
          background-color: #fff;
        }
      }
    }
    ::v-deep .el-tree {
      background: transparent;
      .el-tree-node {
        .el-tree-node__content {
          height: 32px;
          background-color: #fff;
          &:hover {
            background-color: #9bddff !important;
          }
          &.is-current {
            background-color: #6abdff !important;
            color: #fff;
          }
        }
        .el-tree-node__children {
          .el-tree-node {
            .el-tree-node__content {
              background-color: #fff;
            }
            .el-tree-node__children {
              .el-tree-node {
                &:nth-child(odd) {
                  .el-tree-node__content {
                    background-color: #F7F7F7;
                  }
                }
                &:nth-child(even) {
                  .el-tree-node__content {
                    background-color: #fff;
                  }
                }
              }
            }
          }
          .el-tree-node__content {
            padding-left: 24px;
          }
        }
      }
      .el-checkbox {
        margin-right: 8px;
      }
    }
  }
  .add-controls {
    .el-select {
      ::v-deep .el-input__inner {
        background-color: #fff;
      }
    }
  }
  .debug-and-config-container {
    :deep(.el-tabs__content) {
      height: calc(100% - 40px);
      overflow: hidden;
    }
  }
  .config-header {
    padding: 10px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #DCDFE6;
    .config-title {
      font-weight: bold;
    }
    .config-actions {
      display: flex;
      gap: 10px;
    }
  }
}

.ghost {
  opacity: 0.5;
  background: #e6f7ff;
  border: 2px dashed #1890ff;
}

.element-ghost {
  opacity: 0.5;
  background: #e6f7ff;
  border: 2px dashed #1890ff;
}

.preview-dialog-wrapper {
  // z-index: 9999 !important;
}

.preview-dialog {
  margin: 0 !important;
  padding: 0 !important;
}

.preview-dialog .el-dialog__body {
  padding: 0 !important;
  height: 70vh;
}

.preview-dialog .el-dialog__header {
  display: none;
}

.preview-dialog .el-dialog__wrapper {
  background-color: rgba(0, 0, 0, 0.5);
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter,
.slide-leave-to {
  transform: translateX(100%);
}

.revisions-panel {
  position: absolute;
  right: 0;
  top: 0;
  width: 300px;
  height: 100%;
  max-height: 100vh;
  background: #fff;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  z-index: 10;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  overflow-x: hidden;

  .revision-item {
    display: flex;
    gap: 12px;
    padding: 2px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s;
    position: relative;
    padding-left: 20px;

    &.current {
      color: #e6a23c;
      .dot {
        background: #e6a23c;
      }
    }

    &.selected {
      color: #67c23a;
      background-color: #f5f7fa;
      .dot {
        background: #67c23a;
      }
    }

    &:not(.current):not(.selected) {
      color: #909399;
      .dot {
        background: #909399;
      }
    }

    &:hover {
      background: #f5f7fa;
    }

    .dot {
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 8px;
      height: 8px;
      border-radius: 50%;
    }

    &::before {
      content: '';
      position: absolute;
      left: 3px;
      top: 10px;
      width: 2px;
      background: #dcdfe6;
      height: calc(100% - 8px);
    }

    .revision-info {
      flex: 1;
      min-width: 0;

      .revision-title {
        font-size: 13px;
        margin-bottom: 4px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        padding: 2px 4px;
        border-radius: 2px;
        display: inline-block;
      }

      .revision-version {
        font-size: 12px;
        color: #909399;
      }

      .revision-id {
        font-size: 12px;
        color: #909399;
      }

      .revision-preview {
        color: #409eff;
        font-size: 12px;
        text-decoration: none;

        &:hover {
          color: #66b1ff;
        }
      }
    }

    .revision-actions {
      display: flex;
      gap: 8px;
      opacity: 0;
      transition: opacity 0.3s;

      .action-link {
        color: #409eff;
        font-size: 12px;
        text-decoration: none;

        &:hover {
          color: #66b1ff;
        }
      }
    }

    &:hover .revision-actions {
      opacity: 1;
    }
  }
}
</style>
